// Roster — one page that owns Cars / Drivers / Teams. Modeled on the
// track editor's chrome (top toolbar with mode pills, sidebars) but
// without a canvas; the main pane is just the form for the selected
// item.
//
// State held in module-scope variables for the lifetime of the mount.
// `modeState` keeps each mode's selection + dirty state so a quick
// switch back doesn't lose work-in-progress.
//
// URL: #/roster?mode=cars|drivers|teams&id=<entity-id>
// Direct deep-links survive a refresh; the old #/cars / #/edit-car etc.
// routes redirect here.

import { listCars, getCar } from '../api-cars.js';
import { listDrivers, getDriver } from '../api-drivers.js';
import { listTeams, getTeam } from '../api-teams.js';
import { listSeries, getSeries } from '../api-series.js';
import { newEmptyCar } from '../../../shared/car-schema.js';
import { newEmptyDriver, DRIVER_ATTR_DEFAULTS } from '../../../shared/driver-schema.js';
import { newEmptyTeam } from '../../../shared/team-schema.js';
import { newEmptySeries, newEmptyClass } from '../../../shared/series-schema.js';

const MODES = [
  { id: 'cars',    label: 'Cars' },
  { id: 'drivers', label: 'Drivers' },
  { id: 'teams',   label: 'Teams' },
  { id: 'series',  label: 'Series' },
];

// Spec field groups — same layout the standalone car editor used.
const SPEC_GROUPS = [
  ['Mass & power', [
    ['mass',        'Mass',         'kg',     1],
    ['enginePower', 'Engine power', 'W',      1000],
    ['brakeForce',  'Brake force',  'N',      100],
  ]],
  ['Steering', [
    ['maxSteeringAngle', 'Max steer angle',     'rad',   0.01],
    ['steeringRate',     'Steering rate',       'rad/s', 0.1],
    ['steerSpeedRef',    'Authority half-speed','m/s',   0.5],
    ['minSteerMul',      'Min steer mul',       '',      0.01],
  ]],
  ['Grip', [
    ['grip',               'Lateral grip',        'g',    0.05],
    ['tractionGrip',       'Longitudinal grip',   'g',    0.05],
    ['frontGripBias',      'Front grip bias',     '0..1', 0.01],
    ['corneringStiffness', 'Cornering stiffness', '',     0.5],
  ]],
  ['Aero & resistance', [
    ['dragCoeff',         'Drag coefficient',   '',      0.01],
    ['rollingResistance', 'Rolling resistance', 'N·s/m', 1],
  ]],
  ['Chassis', [
    ['wheelbase',  'Wheelbase',   'm',     0.05],
    ['carLength',  'Car length',  'm',     0.1],
    ['carWidth',   'Car width',   'm',     0.05],
    ['yawInertia', 'Yaw inertia', 'kg·m²', 10],
    ['yawDamping', 'Yaw damping', '',      0.05],
  ]],
  ['Consumables & durability', [
    ['fuelCapacity', 'Fuel capacity',  'kg',    1],
    ['fuelBurnRate', 'Fuel burn rate', 'kg/s',  0.005],
    ['durability',   'Durability',     '×',     0.05],
  ]],
];

const DRIVER_ATTR_LABELS = {
  pace:           'Pace',
  consistency:    'Consistency',
  racecraft:      'Racecraft',
  tyreManagement: 'Tyre management',
  wetWeather:     'Wet weather',
  aggression:     'Aggression',
};

const MAX_TEAM_ENTRIES = 4;

export async function render(mountEl) {
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  let mode = MODES.find(m => m.id === params.get('mode'))?.id ?? 'cars';
  let initialId = params.get('id') || null;

  // Per-mode session state. Keeps a partially-filled new entity (or a
  // dirty edit) intact when the user flips between modes mid-flow.
  const modeState = {
    cars:    { selectedId: null, data: null, dirty: false },
    drivers: { selectedId: null, data: null, dirty: false },
    teams:   { selectedId: null, data: null, dirty: false },
    series:  { selectedId: null, data: null, dirty: false },
  };

  document.body.classList.add('edit-mode');
  mountEl.innerHTML = renderShell();

  const titleEl = mountEl.querySelector('#roster-title');
  const modesEl = mountEl.querySelector('#roster-modes');
  const listEl = mountEl.querySelector('#roster-list');
  const formEl = mountEl.querySelector('#roster-form');
  const saveBtn = mountEl.querySelector('#roster-save-btn');
  const dirtyEl = mountEl.querySelector('#roster-dirty');

  // Pre-load list summaries for all three modes once. The team form
  // needs cars + drivers anyway; loading lists for the other two
  // modes up front means switching modes is instant.
  let summaries;
  try {
    const [carsList, driversList, teamsList, seriesList] = await Promise.all([
      listCars(),
      listDrivers(),
      listTeams(),
      listSeries(),
    ]);
    summaries = { cars: carsList, drivers: driversList, teams: teamsList, series: seriesList };
  } catch (err) {
    formEl.innerHTML = `<p class="error">Failed to load roster: ${escapeHtml(err.message)}</p>`;
    return () => { document.body.classList.remove('edit-mode'); };
  }

  // Catalogs (id → object) for the team form's dropdowns. Built lazily
  // since a Cars-only user shouldn't pay for it.
  let teamCatalogs = null;
  const ensureTeamCatalogs = async () => {
    if (teamCatalogs) return teamCatalogs;
    teamCatalogs = { cars: summaries.cars, drivers: summaries.drivers };
    return teamCatalogs;
  };
  // Series form needs the team list (which is already loaded — reuse
  // summaries.teams). Wrapped here for symmetry with ensureTeamCatalogs.
  const ensureSeriesCatalogs = async () => ({ teams: summaries.teams });

  const updateModeUI = () => {
    modesEl.querySelectorAll('button[data-mode]').forEach(b => {
      b.classList.toggle('pill-active', b.dataset.mode === mode);
    });
    titleEl.textContent = MODES.find(m => m.id === mode).label;
  };
  const updateDirtyUI = () => {
    dirtyEl.hidden = !modeState[mode].dirty;
  };
  const setDirty = (v) => {
    modeState[mode].dirty = v;
    updateDirtyUI();
  };

  const renderList = () => {
    const items = summaries[mode];
    const ms = modeState[mode];
    listEl.innerHTML = `
      <button class="panel-btn" id="roster-new">+ New ${singular(mode)}</button>
      <div class="roster-list-items">
        ${items.length === 0
          ? '<p class="muted small">Nothing here yet.</p>'
          : items.map(it => `
              <button class="roster-list-item ${it.id === ms.selectedId ? 'active' : ''}" data-id="${escapeAttr(it.id)}">
                ${listLabelFor(mode, it)}
              </button>
            `).join('')}
      </div>
    `;
    listEl.querySelector('#roster-new').addEventListener('click', () => {
      const fresh = newEmptyFor(mode);
      modeState[mode].selectedId = null;
      modeState[mode].data = fresh;
      modeState[mode].dirty = false;
      renderList();
      renderForm();
      updateUrl();
    });
    listEl.querySelectorAll('.roster-list-item').forEach(b => {
      b.addEventListener('click', async () => {
        if (modeState[mode].dirty && !confirm('Discard unsaved changes?')) return;
        const id = b.dataset.id;
        await loadInto(mode, id);
        renderList();
        renderForm();
        updateUrl();
      });
    });
  };

  const renderForm = async () => {
    const ms = modeState[mode];
    if (!ms.data) {
      formEl.innerHTML = `<p class="muted">Select a ${singular(mode)} on the left, or create a new one.</p>`;
      saveBtn.disabled = true;
      return;
    }
    saveBtn.disabled = false;
    if (mode === 'cars')    renderCarForm(formEl, ms.data, setDirty);
    if (mode === 'drivers') renderDriverForm(formEl, ms.data, setDirty);
    if (mode === 'teams') {
      const cats = await ensureTeamCatalogs();
      renderTeamForm(formEl, ms.data, cats, setDirty, refreshSummariesAfterMissing);
    }
    if (mode === 'series') {
      const cats = await ensureSeriesCatalogs();
      renderSeriesForm(formEl, ms.data, cats, setDirty);
    }
  };

  const refreshSummariesAfterMissing = () => { /* hook reserved */ };

  const loadInto = async (m, id) => {
    if (id == null) {
      modeState[m].data = newEmptyFor(m);
      modeState[m].selectedId = null;
    } else {
      try {
        if (m === 'cars')    modeState[m].data = await getCar(id);
        if (m === 'drivers') modeState[m].data = await getDriver(id);
        if (m === 'teams')   modeState[m].data = await getTeam(id);
        if (m === 'series')  modeState[m].data = await getSeries(id);
        modeState[m].selectedId = id;
      } catch (err) {
        alert(`Failed to load ${m} "${id}": ${err.message}`);
        modeState[m].data = newEmptyFor(m);
        modeState[m].selectedId = null;
      }
    }
    modeState[m].dirty = false;
    updateDirtyUI();
  };

  const updateUrl = () => {
    const ms = modeState[mode];
    const hash = ms.selectedId
      ? `#/roster?mode=${encodeURIComponent(mode)}&id=${encodeURIComponent(ms.selectedId)}`
      : `#/roster?mode=${encodeURIComponent(mode)}`;
    if (window.location.hash !== hash) {
      // Use replaceState so back-button navigation isn't polluted with
      // every selection change. Direct paste of the URL still works.
      history.replaceState(null, '', hash);
    }
  };

  modesEl.addEventListener('click', async ev => {
    const btn = ev.target.closest('button[data-mode]');
    if (!btn) return;
    if (modeState[mode].dirty && !confirm('Discard unsaved changes?')) return;
    mode = btn.dataset.mode;
    updateModeUI();
    updateDirtyUI();
    renderList();
    await renderForm();
    updateUrl();
  });

  saveBtn.addEventListener('click', () => {
    const ms = modeState[mode];
    if (!ms.data) return;
    const name = nameField(mode, ms.data);
    if (!name?.trim()) { alert(`Give the ${singular(mode)} a name first.`); return; }
    let payload = ms.data;
    if (mode === 'teams') {
      // Drop half-filled entries (no driver picked) on save.
      payload = { ...ms.data, entries: ms.data.entries.filter(e => e.driverId) };
    }
    if (mode === 'series') {
      // Drop empty class shells (no name AND no teams) on save —
      // they're scratch rows the user added but never filled in.
      // Keep classes that have a name OR at least one team; users
      // sometimes name a class first and add teams later.
      const cleaned = ms.data.classes.filter(c => (c.name?.trim() || c.teamIds.length > 0));
      payload = {
        ...ms.data,
        classes: cleaned.length > 0 ? cleaned : ms.data.classes,
      };
    }
    downloadJSON(payload, slugify(name) || singular(mode), fileSuffix(mode));
    modeState[mode].dirty = false;
    updateDirtyUI();
  });

  // Initial mount: load whatever the URL asked for, then paint.
  if (initialId) {
    await loadInto(mode, initialId);
  }
  updateModeUI();
  updateDirtyUI();
  renderList();
  await renderForm();

  return () => { document.body.classList.remove('edit-mode'); };
}

// ---------- shell ----------
function renderShell() {
  return `
    <div class="edit-toolbar">
      <div class="edit-toolbar-left">
        <span class="edit-title" id="roster-title">Roster</span>
        <span class="edit-mod-indicator" id="roster-dirty" hidden>● modified</span>
      </div>
      <div class="edit-toolbar-center" id="roster-modes">
        ${MODES.map(m => `<button class="pill" data-mode="${m.id}">${m.label.toUpperCase()}</button>`).join('')}
      </div>
      <div class="edit-toolbar-right">
        <a class="edit-btn" href="#/">Home</a>
        <button class="edit-btn edit-btn-primary" id="roster-save-btn" disabled>Save</button>
      </div>
    </div>
    <div class="edit-stage-row">
      <aside class="edit-side edit-side-l" id="roster-list"></aside>
      <div class="roster-form-area" id="roster-form"></div>
    </div>
  `;
}

// ---------- per-entity forms ----------
function renderCarForm(el, c, setDirty) {
  el.innerHTML = `
    <div class="roster-form">
      <section class="panel-section">
        <h4>Identity</h4>
        <label class="field"><span class="field-label-x">Name</span>
          <input type="text" data-bind="name" value="${escapeAttr(c.name)}">
        </label>
        <label class="field"><span class="field-label-x">Manufacturer</span>
          <input type="text" data-bind="manufacturer" value="${escapeAttr(c.manufacturer)}">
        </label>
        <label class="field"><span class="field-label-x">Description</span>
          <textarea data-bind="description" rows="3">${escapeHtml(c.description)}</textarea>
        </label>
      </section>
      <section class="panel-section">
        <h4>Livery</h4>
        <div class="field" style="flex-direction:row;gap:12px;align-items:center">
          <label style="flex:1"><span class="field-label-x">Primary</span>
            <input type="color" data-livery="primary" value="${escapeAttr(c.livery.primary)}">
          </label>
          <label style="flex:1"><span class="field-label-x">Accent</span>
            <input type="color" data-livery="accent" value="${escapeAttr(c.livery.accent)}">
          </label>
        </div>
      </section>
      ${SPEC_GROUPS.map(([title, fields]) => `
        <section class="panel-section">
          <h4>${title}</h4>
          ${fields.map(([key, label, unit, step]) => `
            <label class="field"><span class="field-label-x">${escapeHtml(label)}${unit ? ` (${escapeHtml(unit)})` : ''}</span>
              <input type="number" step="${step}" data-spec="${escapeAttr(key)}" value="${c.spec[key]}">
            </label>
          `).join('')}
        </section>
      `).join('')}
    </div>
  `;
  el.querySelectorAll('input[data-bind], textarea[data-bind]').forEach(inp => {
    inp.addEventListener('input', e => { c[e.target.dataset.bind] = e.target.value; setDirty(true); });
  });
  el.querySelectorAll('input[data-livery]').forEach(inp => {
    inp.addEventListener('input', e => { c.livery[e.target.dataset.livery] = e.target.value; setDirty(true); });
  });
  el.querySelectorAll('input[data-spec]').forEach(inp => {
    inp.addEventListener('input', e => {
      const v = parseFloat(e.target.value);
      if (Number.isFinite(v)) { c.spec[e.target.dataset.spec] = v; setDirty(true); }
    });
  });
}

function renderDriverForm(el, d, setDirty) {
  el.innerHTML = `
    <div class="roster-form">
      <section class="panel-section">
        <h4>Identity</h4>
        <label class="field"><span class="field-label-x">Name</span>
          <input type="text" data-bind="name" value="${escapeAttr(d.name)}">
        </label>
        <div class="field" style="flex-direction:row;gap:8px">
          <label style="flex:1"><span class="field-label-x">Short (3 letters)</span>
            <input type="text" maxlength="4" data-bind-upper="shortName" value="${escapeAttr(d.shortName)}">
          </label>
          <label style="flex:1"><span class="field-label-x">Nationality</span>
            <input type="text" maxlength="3" data-bind-upper="nationality" value="${escapeAttr(d.nationality)}">
          </label>
          <label style="flex:1"><span class="field-label-x">Number</span>
            <input type="number" min="1" max="999" step="1" data-bind-int="preferredNumber" value="${d.preferredNumber ?? ''}">
          </label>
        </div>
      </section>
      <section class="panel-section">
        <h4>Attributes</h4>
        ${Object.keys(DRIVER_ATTR_DEFAULTS).map(key => {
          const v = d.attributes[key] ?? 0.5;
          return `
            <label class="field">
              <span class="field-label-x">${escapeHtml(DRIVER_ATTR_LABELS[key] ?? key)}
                <span class="field-readout" data-readout="${escapeAttr(key)}">${Math.round(v * 100)}</span></span>
              <input type="range" min="0" max="1" step="0.01" data-attr="${escapeAttr(key)}" value="${v}">
            </label>
          `;
        }).join('')}
      </section>
    </div>
  `;
  el.querySelectorAll('input[data-bind]').forEach(inp => {
    inp.addEventListener('input', e => { d[e.target.dataset.bind] = e.target.value; setDirty(true); });
  });
  el.querySelectorAll('input[data-bind-upper]').forEach(inp => {
    inp.addEventListener('input', e => {
      const v = e.target.value.toUpperCase();
      d[e.target.dataset.bindUpper] = v;
      e.target.value = v;
      setDirty(true);
    });
  });
  el.querySelectorAll('input[data-bind-int]').forEach(inp => {
    inp.addEventListener('input', e => {
      const v = parseInt(e.target.value, 10);
      d[e.target.dataset.bindInt] = Number.isFinite(v) ? v : null;
      setDirty(true);
    });
  });
  el.querySelectorAll('input[data-attr]').forEach(inp => {
    inp.addEventListener('input', e => {
      const key = e.target.dataset.attr;
      const v = parseFloat(e.target.value);
      if (Number.isFinite(v)) {
        d.attributes[key] = v;
        const out = el.querySelector(`[data-readout="${CSS.escape(key)}"]`);
        if (out) out.textContent = Math.round(v * 100);
        setDirty(true);
      }
    });
  });
}

function renderTeamForm(el, t, catalogs, setDirty) {
  const { cars, drivers } = catalogs;
  const driversById = Object.fromEntries(drivers.map(d => [d.id, d]));
  el.innerHTML = `
    <div class="roster-form">
      <section class="panel-section">
        <h4>Identity</h4>
        <label class="field"><span class="field-label-x">Name</span>
          <input type="text" data-bind="name" value="${escapeAttr(t.name)}">
        </label>
        <label class="field"><span class="field-label-x">Short (3 letters)</span>
          <input type="text" maxlength="4" data-bind-upper="shortName" value="${escapeAttr(t.shortName)}">
        </label>
      </section>
      <section class="panel-section">
        <h4>Livery</h4>
        <div class="field" style="flex-direction:row;gap:12px;align-items:center">
          <label style="flex:1"><span class="field-label-x">Primary</span>
            <input type="color" data-livery="primary" value="${escapeAttr(t.livery.primary)}">
          </label>
          <label style="flex:1"><span class="field-label-x">Accent</span>
            <input type="color" data-livery="accent" value="${escapeAttr(t.livery.accent)}">
          </label>
        </div>
      </section>
      <section class="panel-section">
        <h4>Car</h4>
        <label class="field"><span class="field-label-x">Chassis</span>
          <select data-bind-car="carId">
            <option value="" ${t.carId ? '' : 'selected'}>— none —</option>
            ${cars.map(c => `
              <option value="${escapeAttr(c.id)}" ${c.id === t.carId ? 'selected' : ''}>
                ${escapeHtml(c.name)} ${c.manufacturer ? `(${escapeHtml(c.manufacturer)})` : ''}
              </option>
            `).join('')}
          </select>
        </label>
        ${t.carId && !cars.find(c => c.id === t.carId)
          ? `<p class="error small">Missing car: <code>${escapeHtml(t.carId)}</code></p>` : ''}
      </section>
      <section class="panel-section">
        <h4>Drivers</h4>
        <div data-entries></div>
        <button class="panel-btn" data-add-entry>+ Add driver</button>
      </section>
    </div>
  `;
  el.querySelectorAll('input[data-bind]').forEach(inp => {
    inp.addEventListener('input', e => { t[e.target.dataset.bind] = e.target.value; setDirty(true); });
  });
  el.querySelectorAll('input[data-bind-upper]').forEach(inp => {
    inp.addEventListener('input', e => {
      const v = e.target.value.toUpperCase();
      t[e.target.dataset.bindUpper] = v;
      e.target.value = v;
      setDirty(true);
    });
  });
  el.querySelectorAll('input[data-livery]').forEach(inp => {
    inp.addEventListener('input', e => { t.livery[e.target.dataset.livery] = e.target.value; setDirty(true); });
  });
  el.querySelector('[data-bind-car]').addEventListener('change', e => {
    t.carId = e.target.value || null;
    setDirty(true);
  });

  const entriesEl = el.querySelector('[data-entries]');
  const addBtn = el.querySelector('[data-add-entry]');
  const renderEntries = () => {
    entriesEl.innerHTML = t.entries.length === 0
      ? '<p class="muted small">No drivers yet.</p>'
      : t.entries.map((e, idx) => {
          const missing = e.driverId && !driversById[e.driverId];
          return `
            <div class="entry-row" data-idx="${idx}">
              <select data-role="driver">
                <option value="" ${e.driverId ? '' : 'selected'}>— pick a driver —</option>
                ${drivers.map(d => `
                  <option value="${escapeAttr(d.id)}" ${d.id === e.driverId ? 'selected' : ''}>
                    ${escapeHtml(d.name)}${d.shortName ? ` (${escapeHtml(d.shortName)})` : ''}
                  </option>
                `).join('')}
              </select>
              <input type="number" min="1" max="999" step="1" placeholder="#" data-role="num"
                     value="${e.carNumber ?? ''}" style="width:64px">
              <button class="panel-btn panel-btn-danger" data-role="remove" title="Remove">×</button>
              ${missing ? `<span class="error small">missing</span>` : ''}
            </div>
          `;
        }).join('');
    addBtn.disabled = t.entries.length >= MAX_TEAM_ENTRIES;
  };
  renderEntries();

  entriesEl.addEventListener('change', ev => {
    const row = ev.target.closest('.entry-row');
    if (!row) return;
    const e = t.entries[parseInt(row.dataset.idx, 10)];
    if (!e) return;
    if (ev.target.dataset.role === 'driver') e.driverId = ev.target.value;
    if (ev.target.dataset.role === 'num') {
      const n = parseInt(ev.target.value, 10);
      e.carNumber = Number.isFinite(n) ? n : null;
    }
    setDirty(true);
  });
  entriesEl.addEventListener('click', ev => {
    if (ev.target.dataset.role !== 'remove') return;
    const idx = parseInt(ev.target.closest('.entry-row').dataset.idx, 10);
    t.entries.splice(idx, 1);
    setDirty(true);
    renderEntries();
  });
  addBtn.addEventListener('click', () => {
    if (t.entries.length >= MAX_TEAM_ENTRIES) return;
    t.entries.push({ driverId: '', carNumber: null });
    setDirty(true);
    renderEntries();
  });
}

function renderSeriesForm(el, s, catalogs, setDirty) {
  const { teams } = catalogs;
  const teamsById = Object.fromEntries(teams.map(t => [t.id, t]));
  el.innerHTML = `
    <div class="roster-form">
      <section class="panel-section">
        <h4>Identity</h4>
        <label class="field"><span class="field-label-x">Name</span>
          <input type="text" data-bind="name" value="${escapeAttr(s.name)}">
        </label>
        <label class="field"><span class="field-label-x">Short (4 letters)</span>
          <input type="text" maxlength="6" data-bind-upper="shortName" value="${escapeAttr(s.shortName)}">
        </label>
        <label class="field"><span class="field-label-x">Description</span>
          <textarea data-bind="description" rows="2">${escapeHtml(s.description)}</textarea>
        </label>
      </section>
      <section class="panel-section">
        <h4>Classes</h4>
        <p class="muted small">Single-class series (F1, IndyCar) stay with one class. Multi-class series (IMSA, WEC) add a class per category — each team belongs to exactly one class.</p>
        <div data-classes></div>
        <button class="panel-btn" data-add-class>+ Add class</button>
      </section>
    </div>
  `;
  el.querySelectorAll('input[data-bind], textarea[data-bind]').forEach(inp => {
    inp.addEventListener('input', e => { s[e.target.dataset.bind] = e.target.value; setDirty(true); });
  });
  el.querySelectorAll('input[data-bind-upper]').forEach(inp => {
    inp.addEventListener('input', e => {
      const v = e.target.value.toUpperCase();
      s[e.target.dataset.bindUpper] = v;
      e.target.value = v;
      setDirty(true);
    });
  });

  const classesEl = el.querySelector('[data-classes]');
  const addClassBtn = el.querySelector('[data-add-class]');

  // Set of team ids already claimed by some class — used to exclude
  // them from sibling classes' pickers (a team can't sit in two
  // classes within the same series; loadSeries enforces this on read,
  // we enforce it on write so the UI never lets the user create the
  // ambiguity in the first place).
  const claimedExcept = (selfClassId) => {
    const out = new Set();
    for (const c of s.classes) {
      if (c.id === selfClassId) continue;
      for (const tid of c.teamIds) out.add(tid);
    }
    return out;
  };

  const renderClasses = () => {
    classesEl.innerHTML = s.classes.map((cls, idx) => {
      const claimed = claimedExcept(cls.id);
      // Available teams = all teams that aren't claimed by another
      // class. The current class's selected teams render as chips
      // below the picker, with a remove button each.
      const available = teams.filter(t => !claimed.has(t.id) && !cls.teamIds.includes(t.id));
      const selectedChips = cls.teamIds.map(tid => {
        const t = teamsById[tid];
        const swatch = t
          ? `<span class="livery-swatch" style="background:${escapeAttr(t.primary)};border-color:${escapeAttr(t.accent)}"></span>`
          : '';
        const label = t ? escapeHtml(t.name) : `<span class="error small">${escapeHtml(tid)} (missing)</span>`;
        return `
          <span class="series-team-chip">
            ${swatch}${label}
            <button class="chip-x" data-remove-team="${escapeAttr(tid)}" title="Remove">×</button>
          </span>
        `;
      }).join('');
      return `
        <div class="series-class-row" data-class-idx="${idx}">
          <div class="series-class-head">
            <input type="text" placeholder="Class name (e.g. GT3)" data-class-name value="${escapeAttr(cls.name)}">
            <span class="muted small">${cls.teamIds.length} team${cls.teamIds.length === 1 ? '' : 's'}</span>
            <button class="panel-btn panel-btn-danger" data-remove-class title="Remove class" ${s.classes.length <= 1 ? 'disabled' : ''}>×</button>
          </div>
          <div class="series-class-teams">
            ${selectedChips || '<span class="muted small">No teams in this class yet.</span>'}
          </div>
          <div class="series-class-add">
            <select data-add-team>
              <option value="">+ Add team…</option>
              ${available.map(t => `
                <option value="${escapeAttr(t.id)}">
                  ${escapeHtml(t.name)}${t.shortName ? ` (${escapeHtml(t.shortName)})` : ''}
                </option>
              `).join('')}
            </select>
          </div>
        </div>
      `;
    }).join('');
  };
  renderClasses();

  // Class-name edits are inputs (typing); team add is a select change;
  // team remove and class remove are clicks. One delegated listener
  // per event type keeps the wiring compact.
  classesEl.addEventListener('input', ev => {
    if (!ev.target.matches('[data-class-name]')) return;
    const row = ev.target.closest('.series-class-row');
    if (!row) return;
    const cls = s.classes[parseInt(row.dataset.classIdx, 10)];
    if (!cls) return;
    cls.name = ev.target.value;
    setDirty(true);
    // Note: no re-render here — that would steal focus mid-typing.
    // The team-count badge stays as it was when the row was last
    // rendered, which is fine since name edits don't change the count.
  });
  classesEl.addEventListener('change', ev => {
    const row = ev.target.closest('.series-class-row');
    if (!row) return;
    const cls = s.classes[parseInt(row.dataset.classIdx, 10)];
    if (!cls) return;
    if (ev.target.matches('[data-add-team]') && ev.target.value) {
      const tid = ev.target.value;
      if (!cls.teamIds.includes(tid)) {
        cls.teamIds.push(tid);
        setDirty(true);
        renderClasses();
      }
    }
  });
  classesEl.addEventListener('click', ev => {
    const row = ev.target.closest('.series-class-row');
    if (!row) return;
    const idx = parseInt(row.dataset.classIdx, 10);
    const cls = s.classes[idx];
    if (!cls) return;
    if (ev.target.matches('[data-remove-team]')) {
      const tid = ev.target.dataset.removeTeam;
      cls.teamIds = cls.teamIds.filter(x => x !== tid);
      setDirty(true);
      renderClasses();
    } else if (ev.target.matches('[data-remove-class]') && s.classes.length > 1) {
      s.classes.splice(idx, 1);
      setDirty(true);
      renderClasses();
    }
  });

  addClassBtn.addEventListener('click', () => {
    s.classes.push(newEmptyClass(''));
    setDirty(true);
    renderClasses();
  });
}

// ---------- helpers ----------
function singular(mode) {
  if (mode === 'cars') return 'car';
  if (mode === 'drivers') return 'driver';
  if (mode === 'series') return 'series';
  return 'team';
}
function fileSuffix(mode) {
  // singular() returns 'series' for both singular and plural — so the
  // file extension naturally slots in as <slug>.series.json. No
  // override needed.
  return singular(mode);
}
function newEmptyFor(mode) {
  if (mode === 'cars') return newEmptyCar();
  if (mode === 'drivers') return newEmptyDriver();
  if (mode === 'series') return newEmptySeries();
  return newEmptyTeam();
}
function nameField(mode, data) { return data.name ?? ''; }
function listLabelFor(mode, item) {
  if (mode === 'cars') {
    const swatch = `<span class="livery-swatch" style="background:${escapeAttr(item.primary)};border-color:${escapeAttr(item.accent)}"></span>`;
    return `${swatch}${escapeHtml(item.name)}`;
  }
  if (mode === 'drivers') {
    const num = item.preferredNumber != null ? `#${item.preferredNumber}` : '';
    const sub = [item.shortName, num].filter(Boolean).join(' · ');
    return `<strong>${escapeHtml(item.name)}</strong>${sub ? `<span class="muted small">${escapeHtml(sub)}</span>` : ''}`;
  }
  if (mode === 'series') {
    const sub = [
      item.shortName,
      `${item.teamCount} team${item.teamCount === 1 ? '' : 's'}`,
      item.classCount > 1 ? `${item.classCount} classes` : null,
    ].filter(Boolean).join(' · ');
    return `<strong>${escapeHtml(item.name)}</strong>${sub ? `<span class="muted small">${escapeHtml(sub)}</span>` : ''}`;
  }
  const swatch = `<span class="livery-swatch" style="background:${escapeAttr(item.primary)};border-color:${escapeAttr(item.accent)}"></span>`;
  return `${swatch}${escapeHtml(item.name)}`;
}

function slugify(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
function downloadJSON(obj, slug, ext) {
  const json = JSON.stringify(obj, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slug}.${ext}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function escapeAttr(s) { return escapeHtml(s); }
